/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import {
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

// Store
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';
import { TAdditionalityProjectQueryParams } from '@features/catalogs/additionality-projects/list/types';
import {
	TParams,
	TQueryParams,
	TSelectOption,
} from '@global-types/index';
import { useAuthorization } from '@lib/authorization';
import { useElementSize } from '@mantine/hooks';
import useStore from '@store/index';
import {
	cloneDeep,
	removeEmptyArrays,
} from '@utils/functions';
import { TAdditionalityProjectPopulated } from '../types';

/* -------------------------------------------------------------------------- */
/*                                  Hook                                      */
/* -------------------------------------------------------------------------- */

export const useAdditionalities = () => {
	/**************************** Refs ****************************/
	const containerRef: any = useRef<HTMLElement | null>(
		null
	);

	/**************************** States ****************************/
	const [openDrawer, setOpenDrawer] = useState(false);
	const [
		openDrawerAddApiToken,
		setOpenDrawerAddApiToken,
	] = useState(false);
	const { checkAcl, checkAccess } = useAuthorization();

	const [showScrollFilters, setShowScrollFilters] =
		useState(false);
	const [
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
	] = useState(false);

	const [isOverflowFilters] = useState(false);

	const {
		additionalitiesSelectedRow,
		additionalitiesTotalPages,
		additionalitiesPage,
		additionalitiesTotal,
		additionalitiesPageSize,
		setAdditionalitiesPageSize,
		setAdditionalitiesPage,
		setAdditionalitiesSortParams,
		setAdditionalitiesQueryParams,
		additionalitiesQueryParams,
		setAdditionalitiesSortStatus,
		additionalitiesSortStatus,
		additionalitiesSortParams,
		setAdditionalitiesOpenConfirmationModalDelete,
		additionalitiesOpenConfirmationModalDelete,
		setAdditionalitiesSelectedRow,
		setAddAdditionalityProjectFormOpened,
		addAdditionalityProjectFormOpened,
		setUpdateAdditionalityProjectFormOpened,
		updateAdditionalityProjectFormOpened,
		setAdditionalitiesDatatableOptions,
		additionalitiesDatatableOptions,
		setInitialAdditionalityData,
	} = useStore();

	const { ref, width: widthContainer } = useElementSize();
	/**************************** Drawer show more production ****************************/
	const handleAddItem = () => {
		setAddAdditionalityProjectFormOpened(true);
	};

	const handleDeleteMultipleItems = () => {
		setOpenSelectedItemsToDeleteModal(true);
	};

	/**************************** Reset ****************************/
	const handleReset = () => {
		setAdditionalitiesPageSize(100);
		setAdditionalitiesQueryParams({});
		setAdditionalitiesPage(1);
		setAdditionalitiesSortParams('-createdAt');
		setAdditionalitiesSortParams({});
	};

	useEffect(() => {
		return () => {
			handleReset();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useLayoutEffect(() => {
		if (
			containerRef.current &&
			containerRef.current.clientWidth <
				containerRef.current.scrollWidth
		) {
			setShowScrollFilters(true);
		} else {
			setShowScrollFilters(false);
		}
	}, [widthContainer]);

	/**************************** Actions ****************************/
	const handleScroll = (scrollTo: string) => {
		if (containerRef.current) {
			if (scrollTo === 'right') {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft +
					containerRef.current.offsetWidth;
			} else {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft -
					containerRef.current.offsetWidth;
			}
		}
	};
	const handleSortParams = (_column: any) => {
		let sortParams = _column.columnAccessor;

		switch (_column.columnAccessor) {
			case 'unlimited':
				sortParams = 'financing.unlimited';
				break;
			case 'target_euro':
				sortParams = 'financing.target_euro';
				break;
			case 'pledged_euro':
				sortParams = 'financing.pledged_euro';
				break;
			case 'producer':
				sortParams = 'entity';
				break;
			case 'place':
				sortParams = 'location.place';
				break;
			default:
				sortParams;
				break;
		}

		if (_column.direction == 'asc') {
			setAdditionalitiesSortParams(sortParams);
		} else {
			setAdditionalitiesSortParams('-' + sortParams);
		}
	};

	/*************************************** Filters  ***************************************/
	const handleActiveFilter = (status: TQueryParams[]) => {
		setAdditionalitiesPage(1);
		const queryParamsActive = {
			active: status.map(
				(_status: TParams) => _status.value
			),
		};

		setAdditionalitiesQueryParams({
			...additionalitiesQueryParams,
			...queryParamsActive,
		});
	};

	const handleOrganizationFilter = (
		organizations: TQueryParams[]
	) => {
		setAdditionalitiesPage(1);
		const queryParamsProjects = {
			organization: organizations.map(
				(_organization: TParams) => _organization.value
			),
		};

		setAdditionalitiesQueryParams({
			...additionalitiesQueryParams,
			...queryParamsProjects,
		});
	};

	const handleProducerFilter = (
		producers: TQueryParams[]
	) => {
		setAdditionalitiesPage(1);
		const queryParamsProjects = {
			entity: producers.map(
				(_producer: TParams) => _producer.value
			),
		};

		setAdditionalitiesQueryParams({
			...additionalitiesQueryParams,
			...queryParamsProjects,
		});
	};

	const handleCountryFilter = (
		_countries: TSelectOption[]
	) => {
		setAdditionalitiesPage(1);

		const countries = _countries.map(
			(_country: TSelectOption) => _country.value
		);

		if (countries.length == 0) {
			const _offsetAdditionalityProjectsQueryParams =
				cloneDeep(additionalitiesQueryParams);
			delete _offsetAdditionalityProjectsQueryParams[
				'location.countryCode'
			];
			setAdditionalitiesQueryParams(
				_offsetAdditionalityProjectsQueryParams
			);
			return;
		}
		const queryParamsCountries = {
			'location.countryCode': {
				$in: countries,
			},
		};
		const filterParams: TAdditionalityProjectQueryParams =
			removeEmptyArrays({
				...additionalitiesQueryParams,
				...queryParamsCountries,
			});
		setAdditionalitiesQueryParams(filterParams);
	};

	const handleTechnologyFilter = (
		_technologies: TSelectOption[]
	) => {
		setAdditionalitiesPage(1);
		const queryParamsProjects = {
			technology: _technologies.map(
				(_technology: TSelectOption) => _technology.value
			),
		};

		const filterParams: TAdditionalityProjectQueryParams =
			removeEmptyArrays({
				...additionalitiesQueryParams,
				...queryParamsProjects,
			});
		setAdditionalitiesQueryParams(filterParams);
	};

	/*************************************** Delete one Item  ***************************************/

	const handleConfirmDeleteItem = (remove: any) => {
		setAdditionalitiesOpenConfirmationModalDelete({
			open: false,
			params: {},
		});
		remove.mutate(
			additionalitiesOpenConfirmationModalDelete.params.id
		);
	};

	const handleCloseConfirmationDeleteItem = () => {
		setAdditionalitiesOpenConfirmationModalDelete({
			open: false,
			params: {},
		});
	};

	const handleOpenConfirmationDeleteItem = () => {
		/*
		setAdditionalitiesOpenConfirmationModalDelete({
			open: true,
			params: additionalitiesSelectedRow,
		});
        */
	};

	/*************************************** Delete multiple Items  ***************************************/

	/*************************************** Datatables  ***************************************/

	const handleViewMore = (
		row: TAdditionalityProjectPopulated
	) => {
		setAdditionalitiesSelectedRow(row);
		setOpenDrawer(true);
	};

	const handleRowClicked = (row: any) => {
		if (
			checkAcl(ACLS.ADDITIONALITIES.READ) &&
			checkAccess({
				allowedRoles: [
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
				],
			})
		) {
			setAdditionalitiesSelectedRow(row.record);
			setOpenDrawer(true);
		}
	};

	const handleSortStatusChange = (data: any) => {
		handleSortParams(data);
		setAdditionalitiesSortStatus(data);
	};

	/**************************** Pagination ****************************/

	const handlePaginationChangeSize = function (
		_size: string | number
	) {
		setAdditionalitiesPageSize(_size);
		setAdditionalitiesPage(1);
	};

	const handleChangePagination = (data: any) => {
		setAdditionalitiesPage(data);
	};

	/********************************************************************************************/

	return {
		handlePaginationChangeSize,
		handleChangePagination,
		additionalitiesTotalPages,
		additionalitiesPage,
		additionalitiesTotal,
		additionalitiesPageSize,
		handleOpenConfirmationDeleteItem,
		handleCloseConfirmationDeleteItem,
		handleConfirmDeleteItem,
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
		showScrollFilters,
		setShowScrollFilters,
		openDrawerAddApiToken,
		setOpenDrawerAddApiToken,
		openDrawer,
		setOpenDrawer,
		handleOrganizationFilter,
		handleScroll,
		handleSortParams,
		containerRef,
		handleAddItem,
		handleDeleteMultipleItems,
		handleReset,
		ref,
		widthContainer,
		isOverflowFilters,
		handleViewMore,
		handleRowClicked,
		handleSortStatusChange,
		additionalitiesQueryParams,
		handleProducerFilter,
		handleCountryFilter,
		handleTechnologyFilter,
		additionalitiesSortStatus,
		additionalitiesSortParams,
		setAdditionalitiesOpenConfirmationModalDelete,
		additionalitiesOpenConfirmationModalDelete,
		additionalitiesSelectedRow,
		setAddAdditionalityProjectFormOpened,
		addAdditionalityProjectFormOpened,
		setUpdateAdditionalityProjectFormOpened,
		updateAdditionalityProjectFormOpened,
		setAdditionalitiesDatatableOptions,
		additionalitiesDatatableOptions,
		setAdditionalitiesSelectedRow,
		setInitialAdditionalityData,
		handleActiveFilter,
	};
};
